import {Steps} from 'intro.js-react';
import * as React from 'react';
import {isMobile} from '../../utils/helpers';
import {trackOnce, trackToParent} from '../../utils/tracker';

import 'intro.js/introjs.css';
import {
    tab as cssTab,
} from './mobile-tabs.module.sass';
import {
    layout as cssLayout,
} from './session.module.sass';
import {
    pane as cssPane,
    nowBtn as cssNowBtn,
    showSettings as cssShowSettings,
    local as cssLocal,
} from './tutorial-pane.module.sass';
import {
    step as cssStep,
    stepSkip as cssStepSkip,
    highlight as cssHighlight,
} from './tutorial-steps.module.sass';

export enum Tour {
    None = 'None',
    Desktop = 'Desktop',
    Mobile = 'Mobile',
    SwapRoles = 'SwapRoles',
    EndSession = 'EndSession',
    Interviewee = 'Interviewee',
    Interviewer = 'Interviewer',
}

const stepData = {
    welcome: {
        intro: (
            '<h3>Welcome to our practice environment!</h3>'
            + '<br>'
            + 'Here, you can learn how to ace your job interviews by practicing live with other job seekers.'
            + '<br><br>'
            + "<b>It's free, safe, and anonymous. Your data is yours.</b>"
            + '<br><br>'
            + 'Ready to learn more?'
        ),
        eventName: 'welcome',
    },
    howItWorks: {
        intro: (
            '<h3>Here’s how it works:</h3>'
            + '<br>'
            + "Once you're ready, we'll pair you with a fellow job seeker over video chat. You'll take turns interviewing each other, using questions provided by us."
            + '<br><br>'
            + "That means that you'll get to <b>practice answering real world interview questions in front of another person</b>, in exchange for interviewing them too."
        ),
        eventName: 'how-it-works',
    },
    contentTab: {
        element: `.${cssTab}:first-child`,
        intro: "Click the Content tab to see the question you'll ask your peer.",
        position: 'bottom',
        eventName: 'content-tab',
    },
    content: {
        element: '.session-left-bar',
        intro: (
            "When it's your turn to be the interviewer, you'll see this content pane. It contains the questions you'll be asking your peer."
        ),
        position: 'right',
        eventName: 'content',
    },
    swapRoles: {
        element: '.session-swap-roles-button',
        intro: (
            'Every interview is bi-directional: One of you will start as the interviewer, and the other as the interviewee.'
            + '<br><br>'
            + 'Hit the swap roles button half way through, and the interviewer will become the interviewee.'
        ),
        position: 'right',
        eventName: 'swap-roles',
    },
    endSession: {
        element: '.session-end-interview',
        intro: (
            "Once you've both interviewed each other, click here to end the interview and continue on to receive and provide constructive feedback."
        ),
        position: 'left',
        eventName: 'end-session',
    },
    videoTab: {
        element: `.${cssTab}:last-child`,
        intro: 'Click the Video tab to chat with your peer.',
        position: 'bottom',
        eventName: 'video-tab',
    },
    peerVideo: {
        element: `.${cssPane}`,
        intro: (
            "This is where your peer's video will be displayed. Interviews take place here in the browser. No need to install anything!"
        ),
        position: 'left',
        eventName: 'peer-video',
    },
    selfVideo: {
        element: `.${cssLocal}`,
        intro: (
            "Your own video will appear here. <i>Remember that you'll need a working webcam to get started.</i>"
        ),
        position: 'left',
        eventName: 'self-video',
    },
    avSettings: {
        element: `.${cssShowSettings}`,
        intro: (
            'Make sure to test your webcam and microphone by clicking here.'
        ),
        position: 'left',
        eventName: 'av-settings',
    },
    cta: {
        element: `.${cssNowBtn}`,
        intro: (
            'After you review the question set and verify that your webcam is working, click here to get on a live interview.'
        ),
        position: 'left',
        tooltipClass: cssStepSkip,
        eventName: 'cta',
    },
    interviewee: {
        intro: (
            "It's your peer's turn to interview you. When done, click the Swap Roles button."
        ),
        position: 'left',
        tooltipClass: cssStepSkip,
    },
    interviewer: {
        element: `.${cssTab}:first-child`,
        intro: (
            "It's your turn to be the interviewer. Click the Content tab to see your question. When done, click the Swap Roles button."
        ),
        position: 'left',
        tooltipClass: cssStepSkip,
    },
    dummy: {intro: ''},
};

const tours = {
    [Tour.None]: [],
    [Tour.Desktop]: [
        stepData.welcome,
        stepData.howItWorks,
        stepData.content,
        stepData.swapRoles,
        stepData.endSession,
        stepData.peerVideo,
        stepData.selfVideo,
        stepData.avSettings,
        stepData.cta,
    ],
    [Tour.Mobile]: [
        stepData.welcome,
        stepData.howItWorks,
        stepData.contentTab,
        stepData.content,
        stepData.swapRoles,
        stepData.endSession,
        stepData.videoTab,
        stepData.peerVideo,
        stepData.selfVideo,
        stepData.cta,
    ],

    // Intro.js requires at least two steps.
    [Tour.SwapRoles]: [stepData.swapRoles, stepData.cta],
    [Tour.EndSession]: [stepData.endSession, stepData.cta],
    [Tour.Interviewee]: [stepData.interviewee, stepData.dummy],
    [Tour.Interviewer]: [stepData.interviewer, stepData.dummy],
};

const options = {
    disableInteraction: true,
    skipLabel: 'OK',
    doneLabel: 'Got it!',
    exitOnOverlayClick: false,
    helperElementPadding: 0,
    hideNext: true,
    highlightClass: cssHighlight,
    nextLabel: 'Next',
    overlayOpacity: 0,
    showBullets: false,
    showProgress: false,
    showStepNumbers: false,
    keyboardNavigation: false,
    tooltipClass: cssStep,
};

const contentStepIndexOnMobile = tours[Tour.Mobile].indexOf(stepData.content);
const peerVideoStepIndexOnMobile = tours[Tour.Mobile].indexOf(stepData.peerVideo);

export const TutorialSteps: React.StatelessComponent<any> = (
    {onExit, toggleContent, tour}
) => {
    const shouldTrack = tour === Tour.Desktop || tour === Tour.Mobile;
    const trackStep = index => {
        if (index === 0) {
            trackOnce('welcome-zr', {src: 'tutorial'});
            trackToParent('welcome');
        } else {
            const currentSteps = isMobile() ? tours[Tour.Mobile] : tours[Tour.Desktop];
            const {eventName} = currentSteps[index];
            if (eventName) {
                trackOnce(`tutorial-step-${eventName}`);
                trackToParent(`tutorial-step-${eventName}`);
            }
        }

        if (isMobile()) {
            switch (index) {
                case contentStepIndexOnMobile:
                    toggleContent(false);
                    break;
                case peerVideoStepIndexOnMobile:
                    toggleContent(true);
                    break;
              default:
                  // leave as is
            }
        }
    };

    return (
        <Steps
            enabled={tour !== Tour.None}
            steps={tours[tour]}
            initialStep={0}
            onBeforeChange={shouldTrack ? trackStep : undefined}
            onExit={onExit}
            options={options}
            target={`.${cssLayout}`}
        />
    );
};
